import { signal } from '@preact/signals';
import type { EpisodeDto, QueueItemDto } from '../../shared/types';
import { api } from '../api';
import { announce } from '../lib/announce';
import { currentEpisode, upNext } from './player';
import { isSignedIn } from './session';

export const queueItems = signal<QueueItemDto[]>([]);
export const queueLoading = signal(false);

/**
 * Bumped by every write to the queue, whoever makes it.
 *
 * An optimistic mutation snapshots the list so it can put it back if the server refuses.
 * That snapshot is only a safe thing to restore while nothing else has touched the queue in
 * the meantime — otherwise a slow failing request stamps a stale list over a newer, correct
 * one: a failed "add" resurrects an episode a later remove already deleted, a failed
 * "remove" deletes an episode a later add successfully stored, and a refresh from another
 * device gets silently undone.
 *
 * So each mutation records the revision it based itself on and only rolls back if the queue
 * still carries that revision. If it does not, the world has moved on and the right thing is
 * to leave the newer state alone and re-read from the server.
 */
let revision = 0;

function setQueue(items: QueueItemDto[]): number {
  queueItems.value = items;
  return ++revision;
}

/**
 * Undo an optimistic write, but only if it is still the most recent one.
 *
 * Returns whether the rollback happened, so the caller can fall back to re-reading from the
 * server when it did not.
 */
function rollback(
  mine: number,
  items: QueueItemDto[],
  previousUpNext: EpisodeDto[],
): boolean {
  if (revision !== mine) return false;
  setQueue(items);
  upNext.value = previousUpNext;
  return true;
}

/** Re-derive the player's up-next tail from a queue ordering. */
function mirrorUpNext(items: QueueItemDto[]): void {
  const currentId = currentEpisode.value?.id;
  const episodes = items.map((i) => i.episode);
  const index = currentId ? episodes.findIndex((e) => e.id === currentId) : -1;
  upNext.value = index >= 0 ? episodes.slice(index + 1) : episodes;
}

/**
 * Reload the server queue and mirror it into the player's up-next list.
 *
 * The server holds the durable ordering (it syncs across devices); the player only needs
 * the tail after whatever is currently playing.
 */
export async function refreshQueue(): Promise<void> {
  if (!isSignedIn.value) {
    setQueue([]);
    upNext.value = [];
    return;
  }
  queueLoading.value = true;
  try {
    const { items } = await api.queue();
    setQueue(items);
    mirrorUpNext(items);
  } catch {
    // Leave the last known queue in place rather than blanking the view on a blip.
  } finally {
    queueLoading.value = false;
  }
}

/**
 * Add an episode to the queue, showing it there before the server has agreed.
 *
 * The round trip is a hundred-odd milliseconds on a good connection and much worse on a
 * train, and there is nothing to decide in between — the row belongs at the end of the list
 * either way. So the list updates first and the request settles behind it; if the request
 * fails, the optimistic row is pulled back out and the caller is told, which is the only
 * moment the listener needs to know a network existed.
 *
 * Resolves to whether the server accepted, so a caller can revert its own button state.
 */
export async function addToQueue(episode: EpisodeDto): Promise<boolean> {
  if (queueItems.value.some((i) => i.episode.id === episode.id)) return true;

  const optimistic: QueueItemDto = {
    episode,
    // A key past every real one keeps it last until the refresh replaces it with the
    // server's fractional key.
    sortKey: Number.MAX_SAFE_INTEGER,
    addedAt: Date.now(),
  };
  const previousItems = queueItems.value;
  const previousUpNext = upNext.value;

  const mine = setQueue([...previousItems, optimistic]);
  if (currentEpisode.value?.id !== episode.id) upNext.value = [...previousUpNext, episode];

  try {
    await api.enqueue(episode.id);
  } catch {
    announce(`Could not add ${episode.title} to the queue.`);
    // If something newer already replaced what we wrote, re-read rather than stamping a
    // stale list over it.
    if (!rollback(mine, previousItems, previousUpNext)) await refreshQueue();
    return false;
  }

  announce(`Added ${episode.title} to the queue.`);
  await refreshQueue();
  return true;
}

export async function removeFromQueue(episodeId: number): Promise<void> {
  const previousItems = queueItems.value;
  const previousUpNext = upNext.value;
  const removed = previousItems.find((i) => i.episode.id === episodeId)?.episode;

  const mine = setQueue(previousItems.filter((i) => i.episode.id !== episodeId));
  upNext.value = previousUpNext.filter((e) => e.id !== episodeId);

  try {
    await api.dequeue(episodeId);
  } catch {
    // Put it back rather than leave the listener looking at a queue the server disagrees with.
    announce('Could not remove that episode from the queue.');
    if (!rollback(mine, previousItems, previousUpNext)) await refreshQueue();
    return;
  }

  announce(removed ? `Removed ${removed.title} from the queue.` : 'Removed from the queue.');
  await refreshQueue();
}

/**
 * Move an item to a new index.
 *
 * The list is reordered locally first so the drag feels instant, then the neighbours are
 * sent to the server, which computes a single fractional key rather than renumbering.
 */
export async function moveInQueue(episodeId: number, toIndex: number): Promise<void> {
  const previous = queueItems.value;
  const items = [...previous];
  const from = items.findIndex((i) => i.episode.id === episodeId);
  if (from === -1) return;

  const clamped = Math.max(0, Math.min(toIndex, items.length - 1));
  if (clamped === from) return;

  const [moved] = items.splice(from, 1);
  items.splice(clamped, 0, moved);
  const previousUpNext = upNext.value;
  const mine = setQueue(items);
  // `upNext` is the same list seen from the current episode onwards, so a reorder has to
  // move it too. Left until the trailing refresh, an episode that ends mid-request — or a
  // press of Next — would play from the pre-reorder order.
  mirrorUpNext(items);

  const beforeEpisodeId = clamped > 0 ? items[clamped - 1].episode.id : null;
  const afterEpisodeId = clamped < items.length - 1 ? items[clamped + 1].episode.id : null;

  try {
    await api.reorderQueue({ episodeId, beforeEpisodeId, afterEpisodeId });
  } catch {
    announce('Could not reorder the queue.');
    if (!rollback(mine, previous, previousUpNext)) await refreshQueue();
    return;
  }

  announce(`Moved ${moved.episode.title} to position ${clamped + 1} of ${items.length}.`);
  await refreshQueue();
}
